import {useCallback} from 'react';
import {FixedSizeGrid as Grid} from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import {useScopeTranslation} from '@uigstudio/uig-nextjs-helper';

import MapListSwitch from '~components/molecules/MapListSwitch';
import LandPanel from '~components/molecules/LandPanel';
import {RowProps} from '~types/List';
import {FiltersMenu} from '~components/molecules/FiltersMenu';
import {DesktopWalletMenu} from '~components/molecules/DesktopWalletMenu';
import {Loader} from '~components/atoms/Loader';
import NoPlotsFound from '~components/molecules/NoPlotsFound';
import {ApiEndpoints} from '~constants/api';
import useListItems from '~organisms/LandList/useListItems';
import {If} from '~atoms/If';
import SortSelect from '~molecules/SortSelect';

import {
    FoundedPlotsText,
    HeaderContainer,
    ItemLoader,
    LandListContainer,
    ListContainer,
    ListControls,
    ListInfo,
    ListPageWrapper,
    LoaderContainer,
    Sep,
} from './styles';

export type LandListProps = {
    endpoint: ApiEndpoints;
    withWallet?: boolean;
};

const LandList = ({endpoint, withWallet = false, ...props}: LandListProps) => {
    const t = useScopeTranslation('list', 'list');
    const {
        data,
        isLoading,
        totalCount,
        loadNextPage,
        items,
        itemCount,
        numColumns,
        isItemLoaded,
    } = useListItems({
        endpoint,
        withWallet,
    });

    const Row = useCallback(
        ({columnIndex, rowIndex, style}: RowProps) => {
            const itemIndex = rowIndex * numColumns + columnIndex;
            return (
                <div style={style}>
                    {items.length > itemIndex ? (
                        <LandPanel item={items[itemIndex]} />
                    ) : totalCount > itemIndex ? (
                        <ItemLoader>
                            <Loader />
                        </ItemLoader>
                    ) : null}
                </div>
            );
        },
        [numColumns, items, totalCount],
    );

    return (
        <ListPageWrapper {...props}>
            <FiltersMenu openBtnStyle="black" alwaysOpenOnDesktop isList />
            <DesktopWalletMenu color="black" />
            <LandListContainer>
                <HeaderContainer>
                    <ListInfo>
                        <FoundedPlotsText>
                            {totalCount ? t('found', {count: totalCount}) : ''}
                        </FoundedPlotsText>
                        <Sep />
                    </ListInfo>
                    <ListControls>
                        <SortSelect />
                        <MapListSwitch color="black" />
                    </ListControls>
                </HeaderContainer>
                <If condition={!items.length && isLoading}>
                    <LoaderContainer>
                        <Loader withInfo />
                    </LoaderContainer>
                </If>
                <If condition={!items.length && !data?.items.length && !isLoading}>
                    <NoPlotsFound color="black" />
                </If>
                <If condition={items.length}>
                    <ListContainer>
                        <AutoSizer>
                            {({width, height}) => (
                                <InfiniteLoader
                                    isItemLoaded={isItemLoaded}
                                    loadMoreItems={loadNextPage}
                                    itemCount={itemCount}
                                    threshold={20}
                                >
                                    {({onItemsRendered, ref}) => (
                                        <Grid
                                            className="List"
                                            columnCount={numColumns}
                                            columnWidth={width > 370 ? 370 : width}
                                            //subtract 70px to make a spacing that was taken inside 64px margin
                                            height={height - 64}
                                            width={width}
                                            ref={ref}
                                            rowHeight={416 + 16}
                                            rowCount={Math.ceil(
                                                itemCount / numColumns,
                                            )}
                                            onItemsRendered={(gridProps) => {
                                                onItemsRendered({
                                                    overscanStartIndex:
                                                        gridProps.overscanRowStartIndex *
                                                        numColumns,
                                                    overscanStopIndex:
                                                        gridProps.overscanRowStopIndex *
                                                        numColumns,
                                                    visibleStartIndex:
                                                        gridProps.visibleRowStartIndex *
                                                        numColumns,
                                                    visibleStopIndex:
                                                        gridProps.visibleRowStopIndex *
                                                        numColumns,
                                                });
                                            }}
                                        >
                                            {Row}
                                        </Grid>
                                    )}
                                </InfiniteLoader>
                            )}
                        </AutoSizer>
                    </ListContainer>
                </If>
            </LandListContainer>
        </ListPageWrapper>
    );
};

export default LandList;
